import { UntypedFormGroup } from "@angular/forms";
import _, { groupBy, uniqBy } from "lodash";
import { Cellstyle, HotCellText } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";

import {
    Action,
    FieldValue,
    IFieldDecleration,
    InputField,
    InputMode,
    PromptInput,
    PromptResponse
} from "~app/common/components/modal/common/prompt-dialog/prompt-dialog.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ProduktmatrisGrid } from "../../produktinstruktioner-details/matris/ProduktmatrisGrid";
import { ILangdklass } from "../IProduktdefinition";
import { ProduktdefinitionHeaderData } from "../ProduktdefinitionHeaderData";

export class BaseMatrix {
    editIcon = "fas fa-edit";
    showVardematrisPrisrad = false;
    antalDiffandeRadpriser = 0;
    // eslint-disable-next-line sonarjs/public-static-readonly
    static rowHeaderCellName = "rowheader";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static settingsCellName = "settings";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static editAllLangdklasserCellName = "editAlllangdklasser";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static addIcon = "fas fa-plus";

    constructor(
        protected grid: ProduktmatrisGrid,
        protected readonly form: UntypedFormGroup,
        protected readonly headerData: ProduktdefinitionHeaderData,
        protected readonly canEdit: boolean) {
    }

    outsideClickDeselector = (element) => {
        const set = new Set<string>();
        set.add("BUTTON");
        set.add("RASK-MODAL");
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.hasParentNodeTag(element, set)) {
            return false;
        }
        return true;
    };

    getCellsFromSelection(hotDoc: HotDoc, cells: any[]) {
        return cells.map(c => ({
            row: c[0],
            col: c[1],
            cell: hotDoc.rows[c[0]].cells[c[1]]
        }));
    }

    protected hasParentNodeTag(el: any, tags: Set<string>) {
        if (tags.has(el.tagName)) {
            return true;
        }

        while (el.parentNode) {
            el = el.parentNode;
            if (tags.has(el.tagName)) {
                return true;
            }
        }
        return false;
    }

    protected readonly handleGridAdjustment = <T extends IFieldDecleration>(
        doc: HotDoc,
        input: PromptInput<T>,
        fieldValue: FieldValue,
        response: PromptResponse<T>,
        onDelete: (value: any) => void,
        onAdd: (value: any) => void,
        onModified: (oldVal: any, newVal: any) => void) => {
        const inputValue = input.fields.inputField.value;
        if (response.action === Action.delete) {
            onDelete(inputValue);
            doc.rebind();
            return null;
        }

        const newValue = fieldValue.value;
        if (!newValue) {
            return inputValue;
        }

        if (input.mode === InputMode.isNew) {
            onAdd(newValue);

        } else {
            onModified(inputValue, newValue);
        }

        doc.rebind();
        return newValue;
    };

    protected isRowsUnique(values: InputField[], errorMessage: string) {
        const grupperat = groupBy(values, x => x.value);
        const messages: IMessageModel[] = [];
        Object.keys(grupperat).forEach(key => {
            if (grupperat[key].length > 1) {
                messages.push({
                    severity: MessageSeverity.Error,
                    message: errorMessage
                } as IMessageModel);
            }
        });
        return uniqBy(messages, x => x.message);
    }

    protected addEditColumn(columns: IHotColumnDefinition[], canDeleteAll: boolean) {
        if (this.canEdit) {
            const btnHtml =
                `<button class='btn btn-icon icon-btn accordion-trigger' style="line-height:0" type='button' ignore-readonly><i class="column-icon fa fa-trash-alt"></i></button>`;
            columns.push({
                header: canDeleteAll ? btnHtml : ``,
                width: 30,
                readOnly: true,
                cellName: BaseMatrix.settingsCellName
            });
        }
    }

    protected readonly sharedSettings = (hotDoc, raskModalService: RaskModalService, extraSettings?: IHotDocSettings): IHotDocSettings => _.merge({
        raskSettings: {
            calculateSum: false
        },
        stretchH: "none",
        className: "rask-hot head-middle tight"
    } as IHotDocSettings, extraSettings || {});

    protected diameterCellName(startdiameter: number): string {
        return `D${startdiameter.toString()}`;
    }

    protected langdCellName(startlangd: number): string {
        return `L${startlangd.toString()}`;
    }

    protected getLangdklassHeaderCell(langdklass: ILangdklass) {
        const overmal = langdklass.overmal > 0 ? `Ö${langdklass.overmal}` : "";
        return new HotCellText(`${langdklass.startlangd} ${overmal}-`, Cellstyle.Readonly);
    }

    protected readonly getTableHeaderText = (): string => `<i class="fas fa-arrow-down"></i><span>L / D</span><i class="fas fa-arrow-right"></i>`;
    protected readonly getInverseTableHeaderText = (): string => `<i class="fas fa-arrow-down"></i><span>D / L</span><i class="fas fa-arrow-right"></i>`;
    protected readonly getColumnHeaderText = (name: string, icon: string): string => this.canEdit ?
        `<span>${name}</span><button class='btn btn-icon icon-btn accordion-trigger' style="line-height:0" type='button' ignore-readonly>` +
        `<i class="column-icon ${icon}"></i>` +
        `</button>` :
        `<span>${name}</span>`;

    protected readonly getColumnHeaderEditText = (name: string): string => this.canEdit ?
        `<span>${name}</span><button class='btn btn-icon icon-btn accordion-trigger' style="line-height:0" type='button' ignore-readonly>` +
        `<i class="column-icon ${this.editIcon}"></i>` +
        `</button>
            <button class='btn btn-icon icon-btn accordion-trigger' style="line-height:0" type='button' ignore-readonly>` +
        `<i class="column-icon ${BaseMatrix.addIcon}"></i>` +
        `</button>` :
        `<span>${name}</span>`;
}
